import { ofetch } from 'ofetch'
import type { CVTemplate } from '~/types'

interface PuppeteerExportOptions {
  format?: 'A4' | 'Letter'
  margin?: {
    top?: string
    right?: string
    bottom?: string
    left?: string
  }
  printBackground?: boolean
  preferCSSPageSize?: boolean
  landscape?: boolean
  scale?: number
}

interface ExportToPDFParams {
  /** Validated on the server; not needed when `guestToken` is set. */
  cvData?: unknown
  /** Saved CV id; omit for anonymous exports (homepage generator). */
  cvId?: string
  template: CVTemplate
  filename?: string
  options?: PuppeteerExportOptions
  qrCodeData?: string
  /** Spend an export credit for a clean PDF (free users only; premium is always clean). */
  wantClean?: boolean
  /**
   * Paid single export bought without an account: the server renders the CV it
   * parked before checkout, so nothing about the document travels with this call.
   */
  guestToken?: string
  /** A link for one company (/r/<token>): the server prints the CV attached to it. */
  linkToken?: string
  /** Account-less CV this came from (from /api/ai/import-cv); counts the download in admin. */
  generationId?: string | null
}

export function usePuppeteerExport() {
  const isExporting = ref(false)
  /** HTTP status of the last failed export (0 for a network error), for the funnel stats. */
  const lastErrorStatus = ref<number | null>(null)
  const snackbar = useSnackbar()
  const { locale, t } = useI18n() // Musí být na top level

  /**
   * Exportuje CV data do PDF pomocí template-based systému
   */
  async function exportToPDF({
    cvData,
    cvId,
    template,
    filename,
    options = {},
    qrCodeData,
    wantClean = false,
    guestToken,
    linkToken,
    generationId,
  }: ExportToPDFParams): Promise<boolean> {
    if (!cvData && !guestToken && !linkToken) {
      console.error('CV data not found')
      snackbar.show(t('cv.edit.exportError'))
      return false
    }

    try {
      isExporting.value = true
      lastErrorStatus.value = null

      // Volání server API s template-based systémem. Plain ofetch (client-only
      // download): the endpoint returns a binary body, which Nuxt's typed
      // $fetch route inference cannot express.
      const response = await ofetch('/api/pdf/export', {
        method: 'POST',
        body: {
          cvData,
          cvId,
          template,
          filename,
          locale: locale.value,
          qrCodeData,
          wantClean,
          guestToken,
          linkToken,
          generationId: generationId || undefined,
          options: {
            format: 'A4',
            printBackground: true,
            preferCSSPageSize: true,
            margin: {
              top: '0mm',
              right: '0mm',
              bottom: '0mm',
              left: '0mm',
            },
            ...options,
          },
        },
        responseType: 'blob',
      }) as Blob

      // Stažení PDF souboru
      const blob = new Blob([response], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = filename || `cv-${cvId ?? 'inmyresume'}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      URL.revokeObjectURL(url)

      return true

    } catch (error: unknown) {
      console.error('Template-based PDF export error:', error)

      // ofetch puts the whole error response in `data`, so h3's own `data`
      // payload sits one level further down. That is where the server says
      // which 402 this is: no export credit, or a guest payment still in flight.
      const err = error as {
        statusCode?: number
        statusMessage?: string
        data?: { statusMessage?: string; data?: { code?: string } }
      }
      const code = err?.data?.data?.code
      lastErrorStatus.value = err?.statusCode ?? 0

      // Detailnější logování pro debug
      console.error('Error details:', {
        statusCode: err?.statusCode,
        statusMessage: err?.statusMessage,
        data: err?.data,
        fullError: error
      })

      if (err?.statusCode === 408) {
        snackbar.show(t('cv.export.errors.timeout'))
      } else if (err?.statusCode === 429) {
        snackbar.show(t('cv.export.errors.dailyLimit'))
      } else if (code === 'GUEST_PENDING') {
        snackbar.show(t('cv.export.errors.paymentPending'))
      } else if (err?.statusCode === 410 || err?.statusCode === 404) {
        snackbar.show(t('cv.export.errors.linkExpired'))
      } else if (err?.statusCode === 402) {
        snackbar.show(t('cv.export.errors.noCredit'))
      } else if (err?.statusCode === 403) {
        snackbar.show(t('cv.export.errors.forbidden'))
      } else if (err?.statusCode === 400 || err?.statusCode === 413) {
        snackbar.show(err?.data?.statusMessage || err?.statusMessage || t('cv.export.errors.invalid'))
      } else {
        snackbar.show(err?.data?.statusMessage || err?.statusMessage || t('cv.export.errors.generic'))
      }

      return false
    } finally {
      isExporting.value = false
    }
  }

  return {
    isExporting: readonly(isExporting),
    lastErrorStatus: readonly(lastErrorStatus),
    exportToPDF,
  }
}
